import { useState, useCallback } from 'react';
import { Search, Loader2, Check, ImageIcon } from 'lucide-react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';

import { pexelsService, type PexelsPhoto } from '../api/services/pexels';

interface PexelsImagePickerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onImagesSelected: (files: File[]) => void;
    productName?: string;
}

export function PexelsImagePicker({ open, onOpenChange, onImagesSelected, productName }: PexelsImagePickerProps) {
    const [query, setQuery] = useState(productName || '');
    const [photos, setPhotos] = useState<PexelsPhoto[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [isSearching, setIsSearching] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = useCallback(async (searchPage: number = 1) => {
        if (!query.trim()) return;
        setIsSearching(true);
        try {
            const result = await pexelsService.search(query.trim(), searchPage);
            if (searchPage === 1) {
                setPhotos(result.photos);
            } else {
                setPhotos(prev => [...prev, ...result.photos]);
            }
            setPage(searchPage);
            setTotalResults(result.total_results);
            setHasSearched(true);
        } catch {
            // Error handled silently — search field remains usable
        } finally {
            setIsSearching(false);
        }
    }, [query]);

    const toggleSelect = (photoId: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(photoId)) {
                next.delete(photoId);
            } else {
                next.add(photoId);
            }
            return next;
        });
    };

    const handleAddSelected = async () => {
        const selected = photos.filter(p => selectedIds.has(p.id));
        if (selected.length === 0) return;

        setIsDownloading(true);
        try {
            const files = await Promise.all(selected.map(p => pexelsService.downloadAsFile(p)));
            onImagesSelected(files);
            onOpenChange(false);
            // Reset state
            setSelectedIds(new Set());
            setPhotos([]);
            setQuery(productName || '');
            setHasSearched(false);
        } catch {
            // Download errors are handled by the parent via the upload pipeline
        } finally {
            setIsDownloading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch(1);
        }
    };

    const hasMore = photos.length < totalResults;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ImageIcon className="w-5 h-5" />
                        Search Stock Photos
                    </DialogTitle>
                    <DialogDescription>
                        Search free stock photos from Pexels. Select images to add to your product.
                    </DialogDescription>
                </DialogHeader>

                {/* Search bar */}
                <div className="flex gap-2">
                    <Input
                        placeholder="Search for images..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-grow"
                    />
                    <Button
                        onClick={() => handleSearch(1)}
                        disabled={isSearching || !query.trim()}
                        className="bg-gray-800 hover:bg-gray-900 text-white"
                    >
                        {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                </div>

                {/* Results grid */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    {photos.length > 0 ? (
                        <>
                            <div className="grid grid-cols-3 gap-3 py-2">
                                {photos.map(photo => {
                                    const isSelected = selectedIds.has(photo.id);
                                    return (
                                        <div
                                            key={photo.id}
                                            onClick={() => toggleSelect(photo.id)}
                                            className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-gray-300'}`}
                                        >
                                            <img
                                                src={photo.src.medium}
                                                alt={photo.alt}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            {isSelected && (
                                                <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                                <p className="text-[10px] text-white truncate">
                                                    by {photo.photographer}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {hasMore && (
                                <div className="flex justify-center py-3">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleSearch(page + 1)}
                                        disabled={isSearching}
                                    >
                                        {isSearching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : hasSearched ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                            <p className="text-sm">No images found. Try a different search.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Search className="w-12 h-12 mb-2 opacity-50" />
                            <p className="text-sm">Search for stock photos to get started.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <DialogFooter className="border-t pt-3">
                    <p className="text-[10px] text-gray-400 mr-auto self-center">
                        Photos provided by <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels</a>
                    </p>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddSelected}
                        disabled={selectedIds.size === 0 || isDownloading}
                        className="bg-gray-800 hover:bg-gray-900 text-white"
                    >
                        {isDownloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Add {selectedIds.size > 0 ? `${selectedIds.size} Image${selectedIds.size > 1 ? 's' : ''}` : 'Selected'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
